import React, { ReactElement, useState, ReactNode } from 'react';
import { Flex } from 'rebass/styled-components';
import {
  LayoutProps,
  SpaceProps,
  FlexboxProps,
  PositionProps,
} from 'styled-system';

import {
  StyledTree,
  TreeItem,
  TreeList,
  TreeListItem,
  FileActions,
  FileName,
} from './styles';

import { Dropdown } from 'ui/patterns';
import { Button, Checkbox } from 'ui/components';

import * as Icon from 'assets/icons';

import { apiBaseUrl } from 'utils/urls';
import { confirmAlert } from 'utils/confirm-alert';
import { showErrorToast } from 'utils/showToast';

import { BlobType } from 'types/procedureBuilder';
import {
  TreeProps,
  BrowseOptionType,
  FileItemType,
  FileIconType,
} from '../../types/tree';
import { getToken } from 'hooks/useAuth';

const folderActionsTrigger = () => (
  <Button size="sm" variant="subtle">
    <Icon.DotsVertical
      title="Options"
      fill="var(--color-neutral-contrast-06)"
    />
  </Button>
);

function Tree({
  files,
  setSelectedFile,
  selectedFile,
  toggleAddFolder,
  toggleFileUpload,
  deleteFile,
  toggleOpenImportFile,
  selectMode,
  selectedFiles,
  updatedSelectedFiles,
  account,
  isHidden,
  ...ssProps
}: TreeProps): ReactElement | null {
  const [rootOpened, setRootOpened] = useState(false);
  if (!files) return null;
  return (
    <StyledTree hidden={isHidden} {...ssProps}>
      <TreeListItem>
        <TreeItem
          onClick={() => {
            setRootOpened(!rootOpened);
            setSelectedFile &&
              setSelectedFile({
                Name: 'root',
                Uri: '',
                Type: 'folder',
                Blobs: [],
                Path: '',
              });
          }}
          haveFiles={
            files?.Blobs?.length && files?.Blobs?.length > 0 ? true : false
          }
          active={selectedFile?.Path === ''}
        >
          <Flex flex="1" alignItems="center">
            {files?.Blobs?.length && files?.Blobs?.length > 0 ? (
              <button className={`chevron ${rootOpened ? 'rotate' : ''}`}>
                <Icon.ChevronRight title="Close folder" />
              </button>
            ) : null}
            {rootOpened ? (
              <Icon.FolderOpened className="icon-folder" />
            ) : (
              <Icon.Folder className="icon-folder" />
            )}
            <FileName isFolder={true} className="truncate" title={files?.Name}>
              {files?.Name}
            </FileName>
          </Flex>
        </TreeItem>
        {rootOpened && files?.Blobs && (
          <Browse
            files={files?.Blobs}
            setSelectedFile={setSelectedFile}
            selectedFile={selectedFile}
            toggleAddFolder={toggleAddFolder}
            toggleFileUpload={toggleFileUpload}
            deleteFile={deleteFile}
            importFilesMenu={toggleOpenImportFile ? true : false}
            toggleOpenImportFile={toggleOpenImportFile}
            selectMode={selectMode}
            selectedFiles={selectedFiles}
            updatedSelectedFiles={updatedSelectedFiles}
            account={account}
          />
        )}
      </TreeListItem>
    </StyledTree>
  );
}

function Browse({
  files,
  setSelectedFile,
  selectedFile,
  toggleAddFolder,
  toggleFileUpload,
  deleteFile,
  importFilesMenu,
  toggleOpenImportFile,
  selectMode,
  selectedFiles,
  updatedSelectedFiles,
  account,
}: BrowseOptionType) {
  return (
    <>
      <TreeList>
        <TreeListItem>
          {files.map(file => (
            <FileItem
              key={file.Path + file.Name}
              item={file}
              setSelectedFile={setSelectedFile}
              selectedFile={selectedFile}
              toggleAddFolder={toggleAddFolder}
              toggleFileUpload={toggleFileUpload}
              deleteFile={deleteFile}
              importFilesMenu={importFilesMenu}
              toggleOpenImportFile={toggleOpenImportFile}
              selectMode={selectMode}
              selectedFiles={selectedFiles}
              updatedSelectedFiles={updatedSelectedFiles}
              account={account}
            />
          ))}
        </TreeListItem>
      </TreeList>
    </>
  );
}

function FileItem({
  item,
  setSelectedFile,
  selectedFile,
  toggleAddFolder,
  toggleFileUpload,
  deleteFile,
  importFilesMenu,
  toggleOpenImportFile,
  selectMode,
  selectedFiles,
  updatedSelectedFiles,
  account,
}: FileItemType) {
  const [isOpened, setOpened] = useState(false);
  function getMenuList(item: BlobType) {
    const addFolder = {
      value: 'Add Folder',
      icon: <Icon.FolderAdd title="Add folder" />,
      groupEnd: true,
      action: () => {
        toggleAddFolder &&
          toggleAddFolder({
            ...item,
          });
      },
    };
    const uploadFolder = {
      value: 'Upload Files',
      icon: <Icon.Upload title="Upload files" />,
      groupEnd: true,
      action: () => {
        toggleFileUpload &&
          toggleFileUpload({
            ...item,
          });
      },
    };
    const deleteFolder = {
      value: 'Delete Folder',
      icon: <Icon.Delete title="Delete folder" />,
      groupEnd: true,
      action: () => {
        if (item.Blobs && item.Blobs.length > 0) {
          showErrorToast({
            message:
              'Please try after deleting all sub- directories and files.',
          });
        } else {
          deleteFile &&
            deleteFile({
              ...item,
            });
        }
      },
    };
    const importFiles = {
      value: 'Import Files',
      icon: <Icon.Import title="Options" />,
      groupEnd: true,
      action: () => {
        toggleOpenImportFile &&
          toggleOpenImportFile({
            ...item,
          });
      },
    };
    let menus = [];
    if (importFilesMenu) menus = [addFolder, importFiles, uploadFolder];
    else menus = [addFolder, uploadFolder];
    const folders = item.Path.split('/');
    if (account && folders.length > 2 && folders.indexOf(account) === 0) {
      menus.push(deleteFolder);
    }
    return menus;
  }
  const isSelected = selectedFiles?.find(
    selectedFile => selectedFile.Path === item.Path,
  );
  function getAllFiles(folder: BlobType): Array<BlobType> {
    const fileList: Array<BlobType> = [];
    function recursiveToAccumulate(f: BlobType) {
      if (f && f.Type === 'folder' && f.Blobs) {
        f.Blobs.forEach(blob => {
          recursiveToAccumulate(blob);
        });
      }
      fileList.push(f);
    }
    recursiveToAccumulate(folder);
    return fileList.reverse();
  }
  if (selectMode === true && item.Path === 'SharedFiles/') return null;
  return (
    <>
      <TreeItem
        onClick={() => {
          if (item?.Type === 'folder') setOpened(!isOpened);
          // else
          setSelectedFile && setSelectedFile(item);
        }}
        haveFiles={
          item?.Type !== 'folder' ||
            (item?.Blobs?.length && item?.Blobs?.length > 0)
            ? true
            : false
        }
        active={selectedFile?.Path === item?.Path}
      >
        <Flex flex="1" alignItems="center">
          {item?.Blobs?.length && item?.Blobs?.length > 0 ? (
            <button className={`chevron ${isOpened ? 'rotate' : ''}`}>
              <Icon.ChevronRight title="Close folder" />
            </button>
          ) : null}
          <FileIcon fileType={item.Type} isOpened={isOpened} />
          {selectMode === true ? (
            <Checkbox
              handleChange={() => {
                if (updatedSelectedFiles) {
                  let files: Array<BlobType> = [];
                  if (item.Type === 'folder') {
                    files = item.Blobs
                      ? getAllFiles(item) //item.BlobFile?.map(blob => ({ ...blob }))
                      : [];
                  } else {
                    files = [{ ...item }];
                  }
                  updatedSelectedFiles([...files]);
                }
              }}
              checked={!!isSelected}
              label={item?.Name}
              name={item.Type + item?.Path}
            />
          ) : (
            <FileName
              isFolder={item?.Type === 'folder'}
              className="truncate"
              title={item.Name}
            >
              {item.Name}
            </FileName>
          )}
        </Flex>
        {!selectMode ? (
          <>
            {item?.Type === 'folder' ? (
              <Dropdown
                trigger={folderActionsTrigger()}
                menuList={getMenuList(item)}
                position="left"
                className="dd-listitem-options"
              />
            ) : (
              <FileActions>
                {importFilesMenu && (
                  <button
                    onClick={async () => {
                      const url = `${apiBaseUrl}accounts/procedures/get-external-file?filepath=root/${item.Path}`;
                      const response = await fetch(url, {
                        method: "GET",
                        headers: {
                          "Authorization": `Bearer ${getToken()}`,
                        },
                      });
                      const blob = await response.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = blobUrl;
                      a.download = item.Path.split('/').pop() || 'file';
                      a.click();
                      URL.revokeObjectURL(blobUrl);
                    }}
                  >
                    <Icon.Download />
                  </button>
                )}
                {account && item.Path.split('/').indexOf(account) === 0 ? (
                  <button
                    onClick={() => {
                      confirmAlert({
                        message: 'Do you want to delete the file?',
                        buttons: [
                          {
                            label: 'Yes',
                            onClick: () => {
                              deleteFile &&
                                deleteFile({
                                  ...item,
                                });
                            },
                            className: 'pr-btn-primary',
                          },
                          {
                            label: 'No',
                            onClick: () => {
                              //
                            },
                          },
                        ],
                      });
                    }}
                  >
                    <Icon.Delete />
                  </button>
                ) : null}
              </FileActions>
            )}
          </>
        ) : null}
      </TreeItem>
      {isOpened && item?.Blobs && (
        <Browse
          files={item?.Blobs}
          setSelectedFile={setSelectedFile}
          selectedFile={selectedFile}
          toggleAddFolder={toggleAddFolder}
          toggleFileUpload={toggleFileUpload}
          deleteFile={deleteFile}
          importFilesMenu={importFilesMenu}
          toggleOpenImportFile={toggleOpenImportFile}
          selectMode={selectMode}
          selectedFiles={selectedFiles}
          updatedSelectedFiles={updatedSelectedFiles}
          account={account}
        />
      )}
    </>
  );
}

function FileIcon({ fileType, isOpened }: FileIconType) {
  if (fileType === 'image') {
    return <Icon.Image className="icon-file" />;
  } else if (fileType === 'html') {
    return <Icon.Code className="icon-file" />;
  } else if (fileType === 'pdf') {
    return <Icon.Document className="icon-file" />;
  } else if (fileType === 'folder') {
    return (
      <>
        {isOpened ? (
          <Icon.FolderOpened className="icon-folder" />
        ) : (
          <Icon.Folder className="icon-folder" />
        )}
      </>
    );
  }
  return <Icon.Document className="filefolder" />;
}

export { Tree };
